/**
 * BroadcastChannel service for cross-tab communication
 * Replaces localStorage-based messaging with a more efficient and cleaner approach
 * Author: fabrice.miras@vitecsoftware.com
 * Date: 2025-10-03
 */

export interface BroadcastMessage {
  command: string;
  tabId?: string;
  isLogged?: number;
  loginStateUI?: number;
  isActive?: boolean;
  timeStamp?: string;
  random?: string;
  data?: any;
}

export interface TokenData {
  token: string;
  sessionData?: Record<string, string>;
}

export class TabCommunicationService {
  private channel: BroadcastChannel;
  private messageHandlers: Map<string, (message: BroadcastMessage) => void> = new Map();
  
  constructor(channelName = 'autodata-tab-communication') {
    this.channel = new BroadcastChannel(channelName);
    this.channel.addEventListener('message', this.handleMessage.bind(this));
  }

  /**
   * Broadcast a message to all tabs
   * @param message - The message to broadcast
   * @param delay - Optional delay in milliseconds
   */
  broadcast(message: BroadcastMessage, delay = 0): void {
    const broadcastMessage = {
      ...message,
      timeStamp: message.timeStamp || new Date().getTime().toString(),
      random: message.random || (new Date().getTime().toString() + Math.random()),
    };

    if (delay > 0) {
      setTimeout(() => {
        this.channel.postMessage(broadcastMessage);
      }, delay);
    } else {
      this.channel.postMessage(broadcastMessage);
    }
  }

  /**
   * Register a handler for specific message commands
   * @param command - The command to listen for
   * @param handler - The handler function
   */
  onMessage(command: string, handler: (message: BroadcastMessage) => void): void {
    this.messageHandlers.set(command, handler);
  }

  /**
   * Remove a message handler
   * @param command - The command to stop listening for
   */
  offMessage(command: string): void {
    this.messageHandlers.delete(command);
  }

  /**
   * Handle incoming messages and route to appropriate handlers
   * @param event - The message event
   */
  private handleMessage(event: MessageEvent<BroadcastMessage>): void {
    const message = event.data;
    if (!message || !message.command) return;

    const handler = this.messageHandlers.get(message.command);
    if (handler) {
      handler(message);
    }

    // Always call the global message handler if it exists
    const globalHandler = this.messageHandlers.get('*');
    if (globalHandler) {
      globalHandler(message);
    }
  }

  /**
   * Request session storage from other tabs
   * This replaces the localStorage-based session sharing mechanism
   */
  requestSessionStorage(): void {
    this.broadcast({
      command: 'REQUEST_SESSION_STORAGE'
    });
  }

  /**
   * Send session storage data to requesting tab
   * @param sessionData - The session storage data to share
   */
  sendSessionStorage(sessionData: Record<string, string>): void {
    this.broadcast({
      command: 'SEND_SESSION_STORAGE',
      data: sessionData
    });
  }

  /**
   * Request token from other tabs
   * This is used when current tab doesn't have a valid token
   */
  requestToken(): void {
    this.broadcast({
      command: 'TOKEN_REQUEST'
    });
  }

  /**
   * Send token data to requesting tab
   * @param tokenData - The token and session data to share
   */
  sendToken(tokenData: TokenData): void {
    this.broadcast({
      command: 'TOKEN_RESPONSE',
      data: tokenData
    });
  }

  /**
   * Clean up the service
   */
  destroy(): void {
    this.messageHandlers.clear();
    this.channel.close();
  }
}

// Singleton instance for the application
export const tabCommunication = new TabCommunicationService();